/**
 * Snaps the vertical-resize height to the closest row boundary. No tolerance
 * gap — the height ALWAYS lands on a row boundary as long as at least one
 * eligible boundary exists. Distance is `|boundary - rawHeight|`; ties are
 * broken by taking the later boundary in the input array (so callers can
 * control tie-break by sort order if needed).
 *
 * Returns `rawHeight` unchanged when:
 *  - `boundaries` is `undefined` or empty.
 *  - Every boundary is filtered out by `minHeight` or is non-finite.
 */
export const snapHeightToRowBoundary = (
	rawHeight: number,
	boundaries: number[] | undefined,
	options?: {
		minHeight?: number;
	},
): number => {
	const minHeight = options?.minHeight;

	if (!boundaries || boundaries.length === 0) {
		return rawHeight;
	}

	let bestHeight: number | null = null;
	let bestDistance = Number.POSITIVE_INFINITY;

	for (const boundary of boundaries) {
		if (!Number.isFinite(boundary)) {
			continue;
		}
		if (minHeight !== undefined && boundary < minHeight) {
			continue;
		}
		const distance = Math.abs(boundary - rawHeight);
		if (distance <= bestDistance) {
			bestDistance = distance;
			bestHeight = boundary;
		}
	}

	return bestHeight ?? rawHeight;
};
